import type { ApolloError } from '@apollo/client';
import { useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Check from '@mui/icons-material/Check';
import AttachMoney from '@mui/icons-material/AttachMoney';
import FilterList from '@mui/icons-material/FilterList';
import type { MatterViewModel } from '~/modules/matters/matters.types';
import type { RetainerFieldsFragment } from '~/gql/graphql';
import type { User } from '~/shared/types/connect';
import { hasAnyHiredExpert } from '~/modules/matters/matterExpertUtils';
import * as Sentry from '@sentry/react';
import { useExpertStripeOnboardingLink } from '~/modules/connect/useExpertStripeOnboardingLink';
import { useToast } from '~/shared/contexts/ToastContext';
import { useCurrentUser } from '~/shared/hooks/useCurrentUser';
import RetainerHistory from '../RetainerHistory';
import RetainerCard from '../RetainerCard';
import DropdownFilter from '~/modules/matters/DropdownFilter';
import { ActiveRetainerCard } from './ActiveRetainerCard';
import {
  buildReceiptLinesForPendingRetainer,
  cardActionButtonSx,
  countVisibleRetainerMainListCards,
  getHasRetainersPaperContent,
  getRetainerStatusFilterLabel,
  retainerCardSubtitle,
  retainerCardTitle,
} from './retainersTabUtils';

function getRetainerFilterSectionTitle(
  filter: 'all' | 'pending' | 'active' | 'completed',
): string {
  if (filter === 'all') {
    return 'Active';
  }
  if (filter === 'pending') {
    return 'Pending Retainers';
  }
  if (filter === 'completed') {
    return 'Completed Retainers';
  }
  return 'Active Retainers';
}

export interface RetainersTabContentProps {
  matter: MatterViewModel;
  isMatterClosed: boolean;
  isAttorney: boolean;
  isExpertOnMatter: boolean;
  canExpertRequestRetainer: boolean;
  expertStripeConnected: boolean;
  balance: {
    hoursPurchased: number;
    flatAmountPurchased?: number;
  } | null;
  pendingRequests: RetainerFieldsFragment[];
  activeRetainers: RetainerFieldsFragment[];
  flatRateRetainers: RetainerFieldsFragment[];
  filteredPendingRequests: RetainerFieldsFragment[];
  filteredFlatRateRetainers: RetainerFieldsFragment[];
  filteredActiveRetainers: RetainerFieldsFragment[];
  historyRetainers: RetainerFieldsFragment[];
  isLoadingRetainers: boolean;
  retainersError: ApolloError | undefined;
  noRetainerBalance: boolean;
  hasHoursBalance: boolean;
  pendingApproveLabel: string;
  retainerStatusFilter: 'all' | 'pending' | 'active' | 'completed';
  setRetainerStatusFilter: (
    value: 'all' | 'pending' | 'active' | 'completed',
  ) => void;
  retainerExpertFilter: string;
  setRetainerExpertFilter: (value: string) => void;
  retainerExpertsForFilter: Array<{ id: string; name: string }>;
  getExpertById: (id: string) => User | undefined;
  setAgreementRequestId: (id: string | null) => void;
  setDeclineRequestId: (id: string | null) => void;
  onOpenRequestRetainerModal: () => void;
  markRetainerWorkComplete: (retainerRequestId: string) => Promise<void>;
  /** Retainer ID currently being marked complete; null when no request is in flight. */
  completingRetainerId: string | null;
  setCloseRetainerModal: (value: { retainerId: string } | null) => void;
  setViewAgreementRequestId: (id: string | null) => void;
  setViewOverviewRequestId: (id: string | null) => void;
  openChatWithExpert: (expertId: string) => void;
  markRetainerActive: (requestId: string) => Promise<void>;
  /** Retainer ID currently being marked active; null when no request is in flight. */
  activatingRetainerId: string | null;
  getUnreadCountForExpert: (expertId: string) => number;
}

interface EmptyRetainersBlockProps {
  canExpertRequestRetainer: boolean;
  isAttorney: boolean;
  onRequestRetainer: () => void;
}

const EmptyRetainersBlock = ({
  canExpertRequestRetainer,
  isAttorney,
  onRequestRetainer,
}: EmptyRetainersBlockProps) => (
  <Box sx={{ py: 2 }}>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      No active retainers.
    </Typography>
    {canExpertRequestRetainer && (
      <Button
        variant="outlined"
        size="medium"
        startIcon={<AttachMoney sx={{ fontSize: 18 }} />}
        onClick={onRequestRetainer}
        sx={{ textTransform: 'none' }}
      >
        Request retainer
      </Button>
    )}
    {isAttorney && (
      <Typography variant="body2" color="text.secondary">
        To add more funds, the expert can request another retainer for you to
        approve and pay.
      </Typography>
    )}
  </Box>
);

export const RetainersTabContent = (props: RetainersTabContentProps) => {
  const {
    matter,
    isMatterClosed,
    isAttorney,
    isExpertOnMatter,
    canExpertRequestRetainer,
    expertStripeConnected,
    balance,
    pendingRequests,
    historyRetainers,
    isLoadingRetainers,
    retainersError,
    filteredPendingRequests,
    filteredFlatRateRetainers,
    filteredActiveRetainers,
    noRetainerBalance,
    hasHoursBalance,
    pendingApproveLabel,
    retainerStatusFilter,
    setRetainerStatusFilter,
    retainerExpertFilter,
    setRetainerExpertFilter,
    retainerExpertsForFilter,
    getExpertById,
    setAgreementRequestId,
    setDeclineRequestId,
    onOpenRequestRetainerModal,
    markRetainerWorkComplete,
    completingRetainerId,
    setCloseRetainerModal,
    setViewAgreementRequestId,
    setViewOverviewRequestId,
    openChatWithExpert,
    markRetainerActive,
    activatingRetainerId,
    getUnreadCountForExpert,
  } = props;

  const { showToast } = useToast();
  const { user: currentUserForStripe } = useCurrentUser();
  const hasStripeConnectAccount = Boolean(
    currentUserForStripe?.stripeConnectAccountId,
  );
  const { getOnboardingUrl, isLoading: isStripeOnboardingLoading } =
    useExpertStripeOnboardingLink();

  const handleExpertStripeSetup = async () => {
    try {
      const url = await getOnboardingUrl();
      if (url) {
        window.location.assign(url);
        return;
      }
      showToast(
        'Could not start Stripe onboarding. Please try again.',
        'error',
      );
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'stripe_connect_retainers_tab' },
      });
      const message =
        error instanceof Error
          ? error.message
          : 'Could not start Stripe onboarding. Please try again.';
      showToast(message, 'error');
    }
  };

  const retainersStripeSetupDescription = hasStripeConnectAccount
    ? 'Stripe still needs a bit more before you can request retainers. Continue on Stripe to finish verification (for example tax or identity details).'
    : "Set up your Stripe payout account to request retainers and get paid. You will complete bank and tax details on Stripe's secure site.";
  const retainersStripeSetupButtonLabel = hasStripeConnectAccount
    ? 'Continue in Stripe'
    : 'Set up payout account in Stripe';

  const showFilters =
    pendingRequests.length > 0 ||
    props.flatRateRetainers.length > 0 ||
    props.activeRetainers.length > 0 ||
    isMatterClosed;

  const hasRetainersPaperContent = getHasRetainersPaperContent({
    isAttorney,
    isMatterClosed,
    matterExperts: matter.matterExperts,
    balance,
    pendingRequestsLength: filteredPendingRequests.length,
    canExpertRequestRetainer,
    expertStripeConnected,
  });

  const visibleRetainerMainListCardCount = useMemo(
    () =>
      countVisibleRetainerMainListCards({
        isMatterClosed,
        isAttorney,
        matterExperts: matter.matterExperts,
        balance,
        filteredPendingRequests,
        retainerStatusFilter,
        filteredFlatRateRetainers,
        filteredActiveRetainers,
        canExpertRequestRetainer,
        expertStripeConnected,
      }),
    [
      isMatterClosed,
      isAttorney,
      matter.matterExperts,
      balance,
      filteredPendingRequests,
      retainerStatusFilter,
      filteredFlatRateRetainers,
      filteredActiveRetainers,
      canExpertRequestRetainer,
      expertStripeConnected,
    ],
  );

  return (
    <>
      {retainersError && (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {retainersError.message}
        </Typography>
      )}
      {isLoadingRetainers && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={32} />
        </Box>
      )}
      {showFilters && (
        <TitleAndFiltersRow>
          <TitleAndFiltersGroup>
            <Typography variant="subtitle1" fontWeight={700} component="h2">
              {getRetainerFilterSectionTitle(retainerStatusFilter)}
            </Typography>
            <FiltersStack>
              <DropdownFilter
                ariaLabel="Filter by status"
                labelPrefix="Status:"
                valueLabel={getRetainerStatusFilterLabel(retainerStatusFilter)}
                icon={
                  <FilterList fontSize="small" color="action" aria-hidden />
                }
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'active', label: 'Active' },
                  { value: 'completed', label: 'Completed' },
                ]}
                onSelect={(value) =>
                  setRetainerStatusFilter(
                    value as 'all' | 'pending' | 'active' | 'completed',
                  )
                }
              />
              {isAttorney && (
                <DropdownFilter
                  ariaLabel="Filter by expert"
                  labelPrefix="Expert:"
                  valueLabel={
                    retainerExpertFilter === 'all'
                      ? 'All'
                      : retainerExpertsForFilter.find(
                          (expert) => expert.id === retainerExpertFilter,
                        )?.name ?? 'All'
                  }
                  icon={
                    <FilterList fontSize="small" color="action" aria-hidden />
                  }
                  options={[
                    { value: 'all', label: 'All' },
                    ...retainerExpertsForFilter.map((expert) => ({
                      value: expert.id,
                      label: expert.name,
                    })),
                  ]}
                  onSelect={(value) => setRetainerExpertFilter(value)}
                />
              )}
            </FiltersStack>
          </TitleAndFiltersGroup>
          <TotalLabel variant="body2" color="text.secondary">
            Total: {visibleRetainerMainListCardCount}
          </TotalLabel>
        </TitleAndFiltersRow>
      )}
      {isMatterClosed ? (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            This matter is closed. View past activity below.
          </Typography>
        </Paper>
      ) : (
        (() => {
          const showPendingBranch =
            isAttorney &&
            pendingRequests.length > 0 &&
            noRetainerBalance &&
            (retainerStatusFilter === 'all' ||
              retainerStatusFilter === 'pending') &&
            filteredPendingRequests.length > 0;
          if (showPendingBranch) {
            return (
              <>
                <RetainerListStack>
                  {filteredPendingRequests.map((retainerItem) => (
                    <RetainerCard
                      key={retainerItem.id}
                      title={retainerCardTitle(retainerItem)}
                      subtitle={retainerCardSubtitle(retainerItem)}
                      expert={
                        getExpertById(retainerItem.matterExpert.expertId) ??
                        undefined
                      }
                      receiptLines={[
                        ...buildReceiptLinesForPendingRetainer(retainerItem),
                        {
                          label: 'Status',
                          value: 'Awaiting attorney approval',
                          valueColor: 'error',
                        },
                      ]}
                      primaryActions={
                        <Box
                          sx={{ display: 'flex', gap: 1, width: '100%' }}
                          role="group"
                          aria-label="Card actions"
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Check sx={{ fontSize: 16 }} />}
                            onClick={() =>
                              setAgreementRequestId(retainerItem.id)
                            }
                            sx={cardActionButtonSx}
                          >
                            {pendingApproveLabel}
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setDeclineRequestId(retainerItem.id)}
                            sx={cardActionButtonSx}
                          >
                            Decline
                          </Button>
                        </Box>
                      }
                      onMessageClick={() =>
                        openChatWithExpert(retainerItem.matterExpert.expertId)
                      }
                    />
                  ))}
                </RetainerListStack>
              </>
            );
          }
          const hasPendingCardsInList =
            !isAttorney &&
            pendingRequests.length > 0 &&
            (retainerStatusFilter === 'all' ||
              retainerStatusFilter === 'pending');
          const showFundedRetainersSection =
            !isMatterClosed &&
            (hasHoursBalance ||
              filteredActiveRetainers.length > 0 ||
              filteredFlatRateRetainers.length > 0) &&
            (retainerStatusFilter === 'all' ||
              retainerStatusFilter === 'active' ||
              retainerStatusFilter === 'completed');
          const hasRetainerListContent =
            hasPendingCardsInList || showFundedRetainersSection;

          return (
            <>
              {hasRetainerListContent && (
                <RetainerListStack>
                  {hasPendingCardsInList &&
                    pendingRequests.map((retainerItem) => (
                      <RetainerCard
                        key={retainerItem.id}
                        title={retainerCardTitle(retainerItem)}
                        subtitle={retainerCardSubtitle(retainerItem)}
                        receiptLines={[
                          ...buildReceiptLinesForPendingRetainer(retainerItem),
                          {
                            label: 'Status',
                            value: 'Awaiting attorney approval',
                            valueColor: 'error',
                          },
                        ]}
                        hideFooter={false}
                        onMarkAsClosed={() =>
                          setCloseRetainerModal({
                            retainerId: retainerItem.id,
                          })
                        }
                      />
                    ))}
                  {showFundedRetainersSection &&
                    (filteredActiveRetainers.length > 0 ||
                    filteredFlatRateRetainers.length > 0 ? (
                      <>
                        {filteredActiveRetainers.map((retainerItem) => (
                          <ActiveRetainerCard
                            key={retainerItem.id}
                            retainer={retainerItem}
                            isAttorney={isAttorney}
                            isExpertOnMatter={isExpertOnMatter}
                            getExpertById={getExpertById}
                            markRetainerWorkComplete={markRetainerWorkComplete}
                            completingRetainerId={completingRetainerId}
                            onMarkActive={markRetainerActive}
                            activatingRetainerId={activatingRetainerId}
                            onMarkAsClosed={(retainerId: string) =>
                              setCloseRetainerModal({ retainerId })
                            }
                            onMessageExpert={openChatWithExpert}
                            unreadCount={getUnreadCountForExpert(
                              retainerItem.matterExpert.expertId,
                            )}
                            hideFooter={!isAttorney}
                          />
                        ))}
                        {filteredFlatRateRetainers.map((retainerItem) => (
                          <ActiveRetainerCard
                            key={retainerItem.id}
                            retainer={retainerItem}
                            isAttorney={isAttorney}
                            isExpertOnMatter={isExpertOnMatter}
                            getExpertById={getExpertById}
                            markRetainerWorkComplete={markRetainerWorkComplete}
                            completingRetainerId={completingRetainerId}
                            onMarkActive={markRetainerActive}
                            activatingRetainerId={activatingRetainerId}
                            onMarkAsClosed={(retainerId: string) =>
                              setCloseRetainerModal({ retainerId })
                            }
                            onMessageExpert={openChatWithExpert}
                            unreadCount={getUnreadCountForExpert(
                              retainerItem.matterExpert.expertId,
                            )}
                            hideFooter={!isAttorney}
                          />
                        ))}
                      </>
                    ) : (
                      <EmptyRetainersBlock
                        canExpertRequestRetainer={canExpertRequestRetainer}
                        isAttorney={isAttorney}
                        onRequestRetainer={onOpenRequestRetainerModal}
                      />
                    ))}
                </RetainerListStack>
              )}
              {hasRetainersPaperContent && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                  }}
                >
                  {isAttorney &&
                    hasAnyHiredExpert(matter.matterExperts) &&
                    !hasHoursBalance &&
                    !isMatterClosed &&
                    !(noRetainerBalance && pendingRequests.length === 0) && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        To add more funds, the expert can request another
                        retainer for you to approve and pay.
                      </Typography>
                    )}
                  {isAttorney && pendingRequests.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{ mb: 0.5, color: 'grey.700' }}
                      >
                        Pending Approval
                      </Typography>
                      <RetainerListStack>
                        {pendingRequests.map((retainerItem) => (
                          <RetainerCard
                            key={retainerItem.id}
                            title={retainerCardTitle(retainerItem)}
                            subtitle={retainerCardSubtitle(retainerItem)}
                            expert={
                              getExpertById(
                                retainerItem.matterExpert.expertId,
                              ) ?? undefined
                            }
                            receiptLines={[
                              ...buildReceiptLinesForPendingRetainer(
                                retainerItem,
                              ),
                              {
                                label: 'Status',
                                value: 'Awaiting attorney approval',
                                valueColor: 'error',
                              },
                            ]}
                            primaryActions={
                              <Box
                                sx={{ display: 'flex', gap: 1, width: '100%' }}
                                role="group"
                                aria-label="Card actions"
                              >
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<Check sx={{ fontSize: 16 }} />}
                                  onClick={() =>
                                    setAgreementRequestId(retainerItem.id)
                                  }
                                  sx={cardActionButtonSx}
                                >
                                  {pendingApproveLabel}
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() =>
                                    setDeclineRequestId(retainerItem.id)
                                  }
                                  sx={cardActionButtonSx}
                                >
                                  Decline
                                </Button>
                              </Box>
                            }
                            onMessageClick={() =>
                              openChatWithExpert(
                                retainerItem.matterExpert.expertId,
                              )
                            }
                          />
                        ))}
                      </RetainerListStack>
                    </Box>
                  )}
                  {noRetainerBalance && pendingRequests.length === 0 && (
                    <Stack direction="column" spacing={2}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        color="text.primary"
                      >
                        No retainer on this matter yet
                      </Typography>
                      {isAttorney ? (
                        <Typography variant="body2" color="text.secondary">
                          Add your experts and message them to align on scope
                          and how work will be done.
                        </Typography>
                      ) : (
                        <>
                          <Typography variant="body2" color="text.secondary">
                            A retainer lets the attorney prepay for your work on
                            this matter. You propose the hours and your rate;
                            once they approve and pay, you&apos;ll complete the
                            work once the retainer is funded.
                          </Typography>
                          {(() => {
                            if (isMatterClosed) {
                              return (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mt: 1 }}
                                >
                                  This matter is closed. No new retainers can be
                                  requested. View past activity in Retainer
                                  History below.
                                </Typography>
                              );
                            }
                            if (!canExpertRequestRetainer) {
                              return (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mt: 1 }}
                                >
                                  You must be invited to this matter before you
                                  can request a retainer.
                                </Typography>
                              );
                            }
                            if (expertStripeConnected) {
                              return (
                                <>
                                  <Button
                                    variant="contained"
                                    size="medium"
                                    startIcon={
                                      <AttachMoney sx={{ fontSize: 18 }} />
                                    }
                                    onClick={onOpenRequestRetainerModal}
                                    fullWidth
                                    sx={{
                                      textTransform: 'none',
                                      alignSelf: 'flex-start',
                                    }}
                                  >
                                    Request Retainer
                                  </Button>
                                </>
                              );
                            }
                            return (
                              <>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 0.5 }}
                                >
                                  {retainersStripeSetupDescription}
                                </Typography>
                                <Button
                                  type="button"
                                  variant="outlined"
                                  size="medium"
                                  fullWidth
                                  disabled={isStripeOnboardingLoading}
                                  aria-busy={isStripeOnboardingLoading}
                                  startIcon={
                                    isStripeOnboardingLoading && (
                                      <CircularProgress
                                        size={16}
                                        color="inherit"
                                        aria-hidden
                                      />
                                    )
                                  }
                                  onClick={() => {
                                    void handleExpertStripeSetup();
                                  }}
                                  sx={{
                                    textTransform: 'none',
                                    alignSelf: 'flex-start',
                                  }}
                                >
                                  {isStripeOnboardingLoading
                                    ? 'Opening Stripe…'
                                    : retainersStripeSetupButtonLabel}
                                </Button>
                              </>
                            );
                          })()}
                        </>
                      )}
                    </Stack>
                  )}
                  {noRetainerBalance &&
                    pendingRequests.length > 0 &&
                    canExpertRequestRetainer &&
                    expertStripeConnected && (
                      <Button
                        variant="outlined"
                        size="medium"
                        fullWidth
                        startIcon={<AttachMoney sx={{ fontSize: 18 }} />}
                        onClick={onOpenRequestRetainerModal}
                        sx={{ textTransform: 'none' }}
                      >
                        New Retainer
                      </Button>
                    )}
                </Paper>
              )}
            </>
          );
        })()
      )}

      <Box component="section" sx={{ mt: 3 }}>
        <RetainerHistory
          historyRetainers={historyRetainers}
          getExpertById={getExpertById}
          onViewAgreement={setViewAgreementRequestId}
          onViewOverview={setViewOverviewRequestId}
          hideExpertName={!isAttorney}
        />
      </Box>
    </>
  );
};

const TitleAndFiltersRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
}));

const TitleAndFiltersGroup = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
}));

const FiltersStack = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    flexWrap: 'nowrap',
    '& > *': {
      flex: 1,
      minWidth: 0,
    },
  },
}));

const TotalLabel = styled(Typography)(() => ({
  fontWeight: 500,
}));

const RetainerListStack = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
  '& > *': {
    marginBottom: '0 !important',
  },
}));
